import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * Panel para importar personal subcontratado desde un Excel
 * (LISTADO SUBCONTRATAS.xlsx → hoja TARJETAS FICHAJE).
 *
 * Flujo:
 *   1) Selecciona centro de coste destino (RLS para managers).
 *   2) Selecciona Excel .xlsx
 *   3) Botón "Previsualizar" → llama /api/employees/subcontracted/preview
 *   4) Revisa la tabla (total, nuevos, existentes)
 *   5) Botón "Importar" → llama /api/employees/subcontracted/import (upsert)
 *
 * RLS:
 *   - Admin: ve y puede importar a cualquier centro
 *   - Manager: solo ve sus allowed_centers
 *
 * Props:
 *   - compact (bool): si true, renderiza con padding reducido (ideal para modal)
 *   - onImported (fn): callback tras importación exitosa
 */
export function SubcontratasPanel({ compact = false, onImported }) {
  const { apiFetch, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [centros, setCentros]   = useState([]);
  const [centro,  setCentro]    = useState('');
  const [file,    setFile]      = useState(null);
  const [preview, setPreview]   = useState(null);
  const [busyPreview, setBusyPreview] = useState(false);
  const [busyImport,  setBusyImport]  = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Cargar centros disponibles (RLS-aware)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/kpis/centros');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCentros(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('[Subcontratas] Error loading centros:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Solo se admiten archivos .xlsx');
      return;
    }
    setFile(f);
    setPreview(null);
    setImportResult(null);
  };

  const handlePreview = async () => {
    if (!file) { toast.error('Selecciona un archivo Excel'); return; }
    if (!centro) { toast.error('Selecciona un centro de coste'); return; }
    setBusyPreview(true);
    setPreview(null);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('centro_coste', centro);
      const res = await apiFetch('/api/employees/subcontracted/preview', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || `Error ${res.status}`);
        return;
      }
      setPreview(data);
      toast.success(`Excel leído: ${data.total} fila(s) detectada(s)`);
    } catch (e) {
      console.error('[Subcontratas] preview error:', e);
      toast.error(e?.message === 'Sesión expirada' ? 'Sesión caducada' : 'Error de conexión');
    } finally {
      setBusyPreview(false);
    }
  };

  const handleImport = async () => {
    if (!preview) { toast.error('Previsualiza primero el archivo'); return; }
    if (!window.confirm(`¿Importar ${preview.total} subcontrata(s) al centro "${centro}"?\nLos duplicados se actualizarán automáticamente.`)) return;
    setBusyImport(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('centro_coste', centro);
      const res = await apiFetch('/api/employees/subcontracted/import', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || `Error ${res.status}`);
        return;
      }
      setImportResult(data);
      const msg = `Importación: ${data.inserted} nuevos + ${data.updated} actualizados`;
      if (data.errors?.length) {
        toast.warning(`${msg} (con ${data.errors.length} error(es))`);
      } else {
        toast.success(msg);
      }
      // Reset file inputs so user can do another import
      if (onImported) onImported(data);
    } catch (e) {
      console.error('[Subcontratas] import error:', e);
      toast.error(e?.message === 'Sesión expirada' ? 'Sesión caducada' : 'Error de conexión');
    } finally {
      setBusyImport(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewRowsSample = (preview?.rows || []).slice(0, 8);
  const previewMore = Math.max(0, (preview?.total || 0) - previewRowsSample.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="subcontratas-panel"
      style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: compact ? '18px' : '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
      }}
    >
      <div>
        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>
          Importar Subcontratas
        </h3>
        <p style={{ color: '#64748b', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
          Carga el Excel <code style={{ color: '#94a3b8', fontFamily: 'IBM Plex Mono', fontSize: '12px' }}>LISTADO SUBCONTRATAS.xlsx</code>{' '}
          (hoja <strong>TARJETAS FICHAJE</strong>). El <strong>Nº TARJETA</strong> se usa como ID del empleado para fichajes hardware.
          Los duplicados se actualizan automáticamente (upsert).
        </p>
      </div>

      {/* Selector de centro */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Centro de coste destino *</Label>
        <select
          data-testid="subcontratas-centro-select"
          value={centro}
          onChange={(e) => { setCentro(e.target.value); setPreview(null); setImportResult(null); }}
          style={{
            background: '#0f172a',
            border: '1px solid #334155',
            color: '#f1f5f9',
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '13px',
            width: '100%',
          }}
        >
          <option value="">— Selecciona un centro —</option>
          {centros.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {!isAdmin && centros.length === 0 && (
          <span style={{ color: '#f59e0b', fontSize: '12px' }}>
            No tienes centros asignados. Contacta con un administrador.
          </span>
        )}
        {isAdmin && (
          <span style={{ color: '#475569', fontSize: '11px' }}>
            Como admin puedes asignar a cualquier centro. Los managers solo verán los suyos.
          </span>
        )}
      </div>

      {/* Selector de archivo */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Archivo Excel (.xlsx) *</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFile}
          data-testid="subcontratas-file-input"
          style={{
            background: '#0f172a',
            border: '1px dashed #334155',
            color: '#94a3b8',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '13px',
            cursor: 'pointer',
            width: '100%',
          }}
        />
        {file && (
          <span style={{ color: '#22c55e', fontSize: '12px', fontFamily: 'IBM Plex Mono' }}>
            ✓ {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </span>
        )}
      </div>

      {/* Acciones */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <Button
          onClick={handlePreview}
          disabled={!file || !centro || busyPreview}
          data-testid="subcontratas-preview-btn"
          style={{
            background: '#334155',
            color: '#f1f5f9',
            padding: '0 20px',
            height: '38px',
            fontWeight: 500,
          }}
        >
          {busyPreview ? 'Leyendo...' : '🔍 Previsualizar'}
        </Button>
        <Button
          onClick={handleImport}
          disabled={!preview || busyImport || preview.total === 0}
          data-testid="subcontratas-import-btn"
          style={{
            background: !preview || preview.total === 0 ? '#334155' : '#22c55e',
            color: !preview || preview.total === 0 ? '#64748b' : '#052e16',
            padding: '0 20px',
            height: '38px',
            fontWeight: 600,
          }}
        >
          {busyImport ? 'Importando...' : '📥 Importar'}
        </Button>
        {(file || preview) && (
          <Button
            onClick={handleReset}
            variant="ghost"
            data-testid="subcontratas-reset-btn"
            style={{ color: '#94a3b8' }}
          >
            Limpiar
          </Button>
        )}
      </div>

      {/* Resumen previo a importar */}
      {preview && (
        <div
          data-testid="subcontratas-preview-summary"
          style={{
            background: '#0f172a',
            border: '1px solid #1e3a5f',
            borderRadius: '8px',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
            <Stat label="Total filas" value={preview.total} color="#60a5fa" />
            <Stat label="Nuevos" value={preview.new} color="#22c55e" />
            <Stat label="Ya existen" value={preview.existing} color="#f59e0b" />
          </div>
          {previewRowsSample.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: '8px' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#64748b', textAlign: 'left', borderBottom: '1px solid #334155' }}>
                    <th style={{ padding: '6px 8px' }}>Nº TARJETA</th>
                    <th style={{ padding: '6px 8px' }}>Nombre</th>
                    <th style={{ padding: '6px 8px' }}>Apellidos</th>
                    <th style={{ padding: '6px 8px' }}>Empresa</th>
                    <th style={{ padding: '6px 8px' }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRowsSample.map((r, i) => (
                    <tr key={i} style={{ color: '#94a3b8', borderBottom: '1px solid #1e293b' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'IBM Plex Mono', color: '#cbd5e1' }}>{r.num_tarjeta}</td>
                      <td style={{ padding: '6px 8px' }}>{r.nombre}</td>
                      <td style={{ padding: '6px 8px' }}>{r.apellidos}</td>
                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{r.empresa}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{
                          background: r.exists ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                          color: r.exists ? '#fbbf24' : '#4ade80',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}>{r.exists ? 'Actualizar' : 'Nuevo'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewMore > 0 && (
                <div style={{ color: '#475569', fontSize: '11px', marginTop: '6px' }}>
                  ...y {previewMore} fila(s) más.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Resultado de la importación */}
      {importResult && (
        <div
          data-testid="subcontratas-import-result"
          style={{
            background: importResult.errors?.length ? '#451a03' : '#052e16',
            border: `1px solid ${importResult.errors?.length ? '#92400e' : '#15803d'}`,
            borderRadius: '8px',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 600, color: importResult.errors?.length ? '#fbbf24' : '#4ade80' }}>
            ✓ Importación finalizada — centro: <span style={{ fontFamily: 'IBM Plex Mono' }}>{importResult.centro_coste}</span>
          </div>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '12px', color: '#cbd5e1' }}>
            <span><strong>{importResult.inserted}</strong> nuevos</span>
            <span><strong>{importResult.updated}</strong> actualizados</span>
            <span><strong>{importResult.total}</strong> totales</span>
            {importResult.errors?.length > 0 && (
              <span style={{ color: '#fbbf24' }}><strong>{importResult.errors.length}</strong> error(es)</span>
            )}
          </div>
          {importResult.errors?.length > 0 && (
            <ul style={{ color: '#fbbf24', fontSize: '11px', margin: '4px 0 0', paddingLeft: '20px' }}>
              {importResult.errors.slice(0, 5).map((e, i) => (
                <li key={i}>Nº {e.num_tarjeta}: {e.error}</li>
              ))}
              {importResult.errors.length > 5 && <li>...y {importResult.errors.length - 5} más</li>}
            </ul>
          )}
        </div>
      )}
    </motion.div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ color, fontSize: '22px', fontWeight: 700, fontFamily: 'Space Grotesk' }}>{value}</div>
    </div>
  );
}
